import Chart from "Chart";
import * as preact from "preact";

import * as currentDate from "currentDate";
import * as dailyReminders from "dailyReminders";
import * as deviceDetect from "deviceDetect";
import * as localAPI from "localAPI";
import * as modalController from "modalController";
import * as practiceUtils from "practiceUtils";
import readlangClient from "readlangClient";
import * as sharePrompt from "sharePrompt";
import * as dateUtils from "shared/dateUtils";
import * as sourceUtils from "shared/sourceUtils";
import * as spacedRepetition from "shared/spacedRepetition";
import * as utils from "shared/utils";
import * as signupModal from "signupModal";
import { SoundEffectView } from "soundEffect";
import stores from "stores/stores";
import * as urlUtils from "urlUtils";
import ChatPracticeView from "views/ChatPracticeView";
import Dialog from "views/Dialog";
import PracticeButton from "views/PracticeButton";
import ReadFirstHintView from "views/ReadFirstHintView";
import SkeletonView from "views/SkeletonView";
import SourcePickerModal from "views/SourcePickerModal";

const SELECTED_SOURCE_STORAGE_KEY_PREFIX = "practice.selectedSource.";

const getStoredSelectedSources = (language: string): string[] => {
  const stored = localStorage.getItem(
    SELECTED_SOURCE_STORAGE_KEY_PREFIX + language
  );
  if (!stored) {
    return [];
  }
  return stored.split("|").filter((source) => source.length > 0);
};

const setStoredSelectedSources = (
  language: string,
  sources: string[]
): void => {
  const key = SELECTED_SOURCE_STORAGE_KEY_PREFIX + language;
  if (sources.length > 0) {
    localStorage.setItem(key, sources.join("|"));
  } else {
    localStorage.removeItem(key);
  }
};

const SOURCE_DISPLAY_MAX_LENGTH = 50;

const RECENTLY_READ_INITIAL_LIMIT = 25;

interface Props {
  appRouter: any;
}

type RecallEntry = { start: number; end: number; recallCount: number };

type State = {
  sessionComplete: boolean;
  newPracticeComplete: boolean;
  perfectSession: boolean;

  // Each piece below is undefined while its fetch is in flight; setting it
  // independently is what makes the page render progressively.
  language?: string;
  firstLanguage?: string;
  hasNewPracticeModes?: boolean;
  date?: Date;
  todaysCards?: any[];
  // Total number of words ready to test ("All (N words ready)"), from the
  // inline count of the readyToTest fetch. undefined while in flight.
  readyToTestCount?: number;
  readyToTest?: any[];
  localMode?: boolean;
  sources: any[] | "loading";
  selectedSources?: string[];
  recallHistory?: RecallEntry[] | null;
};

class LearnPageView extends preact.Component<Props, State> {
  private disposeUserChange?: () => void;
  private currentInitializeID = 0;
  private renderedRecallHistory?: RecallEntry[] | null;

  constructor(props: Props) {
    super(props);

    this.disposeUserChange = stores.user.onChange(
      (user) => user,
      this.onUserChange
    );

    this.state = {
      sessionComplete: urlUtils.getUrlVar("sessionComplete") === "true",
      newPracticeComplete: urlUtils.getUrlVar("newPractice") === "true",
      perfectSession: urlUtils.getUrlVar("perfectSession") === "true",
      sources: "loading",
    };

    this.props.appRouter.navigate("/learn", { replace: true });
    this.initialize();
  }

  /**
   * Returns a partial state update for fields that should reset to their
   * initial values when the user switches language (or otherwise re-initializes
   * the page). Keeps the session-status flags on the existing state untouched.
   */
  private resetLoadedFields(): Partial<State> {
    return {
      language: undefined,
      firstLanguage: undefined,
      hasNewPracticeModes: undefined,
      date: undefined,
      todaysCards: undefined,
      readyToTestCount: undefined,
      readyToTest: undefined,
      localMode: undefined,
      sources: "loading",
      selectedSources: undefined,
      recallHistory: undefined,
    };
  }

  initialize = () => {
    const initializeID = ++this.currentInitializeID;
    this.renderedRecallHistory = undefined;

    this.setState(this.resetLoadedFields() as State);

    const isCurrent = () => initializeID === this.currentInitializeID;

    // Capture the language at the start so an in-flight fetch that resolves
    // after a language change can be discarded by the initializeID guard.
    const language = stores.user.currentLearningLanguage.value;
    const firstLanguage = stores.user.firstLanguage.value;

    // Every fetch is fired in parallel and writes its own slice of state as
    // soon as it resolves, so each section of the page renders independently.

    void currentDate
      .getSnapped(stores.user.state.value.updateHour)
      .then((snappedDate) => {
        if (!isCurrent()) return;
        this.setState({
          date: new Date(snappedDate.getTime() + 1000),
        });
      });

    void localAPI.isLocalMode().then((localMode) => {
      if (!isCurrent()) return;
      this.setState({ localMode });
    });

    void practiceUtils.hasNewPracticeModes(stores.user).then((value) => {
      if (!isCurrent()) return;
      this.setState({ hasNewPracticeModes: value });
    });

    // readyToTest gates the practice CTA and recall-chart container, so it
    // must land independently of the slower reviewedToday fetch. Its response
    // also carries the total "words ready" count used by the source picker.
    void stores.practiceStore.fetchGroup("readyToTest").then((words) => {
      if (!isCurrent()) return;
      this.setState({
        readyToTest: words,
        readyToTestCount:
          stores.practiceStore.getReadyToTestCount() ?? undefined,
        language,
        firstLanguage,
      });
    });

    // reviewedToday populates the in-memory word index that completedToday()
    // iterates, so it only gates todaysCards — not the practice CTA.
    void stores.practiceStore.fetchGroup("reviewedToday").then(async () => {
      if (!isCurrent()) return;
      const todaysCards = await stores.practiceStore.completedToday();
      if (!isCurrent()) return;
      this.setState({ todaysCards });
    });

    void this.fetchRecallHistory().then((recallHistory) => {
      if (!isCurrent()) return;
      this.setState({ recallHistory });
    });

    void this.fetchSources(initializeID);
  };

  fetchSources = async (initializeID: number) => {
    const isCurrent = () => initializeID === this.currentInitializeID;

    if (await readlangClient.isLocalMode()) {
      if (!isCurrent()) return;
      this.setState({ sources: [] });
      return;
    }

    const language = stores.user.currentLearningLanguage.value;

    const sources = (
      await readlangClient.request("GET", "/api/sourcesWithWords", {
        wordGroup: "readyToTest",
        language,
        // Practice can draw on a whole shelf; the words page can't.
        includeShelves: true,
      })
    ).data;

    if (!isCurrent()) return;

    sources.sort((a, b) => {
      // Sort by lastReadDate descending (sources without it sink to the bottom).
      const aTime = a.lastReadDate ? new Date(a.lastReadDate).getTime() : 0;
      const bTime = b.lastReadDate ? new Date(b.lastReadDate).getTime() : 0;
      if (aTime !== bTime) {
        return bTime - aTime;
      }
      // Fall back to word count to keep ordering stable / sensible for
      // sources without a lastReadDate (e.g. url:, chat:, manual).
      return b.count - a.count;
    });

    for (const item of sources) {
      item.displayName = utils.elide(
        sourceUtils.formatSourceDisplayName(
          item.source,
          item.title,
          item.author
        ),
        SOURCE_DISPLAY_MAX_LENGTH
      );
    }

    const storedSources = getStoredSelectedSources(language);
    const validSourceKeys = new Set(sources.map((item) => item.source));
    const selectedSources = storedSources.filter((source) =>
      validSourceKeys.has(source)
    );
    if (selectedSources.length !== storedSources.length) {
      setStoredSelectedSources(language, selectedSources);
    }

    this.setState({ sources, selectedSources });
  };

  componentDidMount() {
    if (this.state.sessionComplete) {
      void sharePrompt.practiceCompleted();
    }
  }

  componentWillUnmount() {
    this.currentInitializeID++;
    this.disposeUserChange?.();
  }

  componentDidUpdate() {
    // Draw the recall chart as soon as both the data has landed and the
    // <canvas> exists in the DOM. We compare by reference so a re-fetch on
    // language change will redraw.
    if (
      this.state.recallHistory !== undefined &&
      this.renderedRecallHistory !== this.state.recallHistory
    ) {
      const canvasExists = $("#weeklyChart").length > 0;
      if (canvasExists) {
        this.renderedRecallHistory = this.state.recallHistory;
        this.renderRecallChart(this.state.recallHistory);
      }
    }
  }

  fetchRecallHistory = async (): Promise<RecallEntry[] | null> => {
    if (await readlangClient.isLocalMode()) {
      return null;
    }

    // Calculate 7 full days (midnight to midnight, local time) up to and including today
    const now = new Date();
    const todayLocalMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const ranges = [];
    for (let i = 6; i >= 0; i--) {
      const start = new Date(
        todayLocalMidnight.getFullYear(),
        todayLocalMidnight.getMonth(),
        todayLocalMidnight.getDate() - i
      );
      const end = new Date(
        start.getFullYear(),
        start.getMonth(),
        start.getDate() + 1
      );
      ranges.push({ start: start.getTime(), end: end.getTime() });
    }
    const response = await readlangClient.request(
      "POST",
      "/api/userWords/recallHistoryByRanges",
      {
        ranges,
        language: stores.user.currentLearningLanguage.value,
      }
    );
    return response.data.recallHistory;
  };

  /** This renders by direct DOM manipulation as opposed to using preact. */
  renderRecallChart = (recallHistory: RecallEntry[] | null) => {
    if (!recallHistory) {
      return;
    }

    const days =
      "Sunday Monday Tuesday Wednesday Thursday Friday Saturday".split(" ");

    const chartData: {
      labels: string[];
      datasets: {
        fillColor: string;
        strokeColor: string;
        pointColor: string;
        pointStrokeColor: string;
        data: number[];
      }[];
    } = {
      labels: [],
      datasets: [
        {
          fillColor: "rgba(151,187,235,0.5)",
          strokeColor: "rgba(151,187,235,1)",
          pointColor: "rgba(151,187,235,1)",
          pointStrokeColor: "#fff",
          data: [],
        },
      ],
    };
    let maxCount = 0;
    for (const recall of recallHistory) {
      const day = days[new Date(recall.start).getDay()];
      chartData.labels.push(day);
      chartData.datasets[0].data.push(recall.recallCount);
      if (recall.recallCount > maxCount) {
        maxCount = recall.recallCount;
      }
    }

    if (maxCount > 0) {
      $("#weeklyChartContainerContainer").show();

      const weeklyChartElement = $("#weeklyChart");
      if (weeklyChartElement.length === 0) {
        // This sometimes happens, perhaps in browsers which don't support canvas?
        return;
      }

      if (deviceDetect.isSmall()) {
        weeklyChartElement.attr("width", 288);
      }
      new Chart((weeklyChartElement[0] as any).getContext("2d")).Line(
        chartData,
        {
          scaleOverride: true,
          scaleSteps: 4,
          scaleStepWidth: Math.ceil(Math.max(20, maxCount) / 4),
          scaleStartValue: 0,
          bezierCurve: false,
        }
      );
    }
  };

  onUserChange = () => {
    if (
      this.state.language !== undefined &&
      (stores.user.currentLearningLanguage.value !== this.state.language ||
        stores.user.firstLanguage.value !== this.state.firstLanguage)
    ) {
      // Re-fetch all data on language change
      this.initialize();
    } else {
      this.forceUpdate();
    }
  };

  getDeckSizes = (cardsAvailable) => {
    const result: {
      verySmallDeckSize?: number;
      smallDeckSize?: number;
      mediumDeckSize?: number;
      largeDeckSize?: number;
      veryLargeDeckSize?: number;
    } = {};

    if (cardsAvailable < 11) {
      result.verySmallDeckSize = Math.min(4, cardsAvailable);
      result.smallDeckSize = Math.min(8, cardsAvailable);
    } else if (cardsAvailable < 20) {
      result.verySmallDeckSize = 4;
      result.smallDeckSize = 8;
      result.mediumDeckSize = cardsAvailable;
    } else if (cardsAvailable < 35) {
      result.verySmallDeckSize = 4;
      result.smallDeckSize = 8;
      result.mediumDeckSize = 15;
      result.largeDeckSize = Math.min(25, cardsAvailable);
    } else {
      result.verySmallDeckSize = 4;
      result.smallDeckSize = 8;
      result.mediumDeckSize = 15;
      result.largeDeckSize = 25;
      result.veryLargeDeckSize = Math.min(50, cardsAvailable);
    }
    return result;
  };

  render() {
    const user = stores.user;

    /** This will be empty if no message is meant to be shown */
    const sessionCompleteMessage = (
      <>
        {this.state.sessionComplete ? (
          <>
            {this.state.perfectSession ? (
              <h2 class="perfectSessionMessage">⭐ Perfect session! ⭐</h2>
            ) : (
              <h2>🎉 Session complete! 🎉</h2>
            )}
          </>
        ) : null}
      </>
    );

    const { readyToTest, todaysCards } = this.state;

    // Can't render the layout yet: we need readyToTest to decide between the
    // practice CTA and the empty-state hint, and (when there are no new cards)
    // we need todaysCards too.
    if (readyToTest === undefined) {
      return (
        <>
          {sessionCompleteMessage}
          <SkeletonView kind="blocks" count={3} />
        </>
      );
    }

    const newCardsAvailable = readyToTest.length > 0;

    const sessionCompleteAudio = this.state.sessionComplete ? (
      <SoundEffectView soundEffect="session-complete" />
    ) : null;

    // No new cards available — wait for todaysCards too, because we need it to
    // choose between the first-use hint and the "No New Cards" page.
    if (!newCardsAvailable && todaysCards === undefined) {
      return (
        <>
          {sessionCompleteMessage}
          <SkeletonView kind="blocks" count={3} />
        </>
      );
    }

    if (
      !newCardsAvailable &&
      todaysCards !== undefined &&
      todaysCards.length === 0
    ) {
      return (
        <>
          <ReadFirstHintView>
            The words and phrases you translate while reading will be turned
            into flashcards for you to practice here.
          </ReadFirstHintView>
          <ChatPracticeView />
          {sessionCompleteAudio}
        </>
      );
    }

    const { localMode, date, readyToTestCount, sources } = this.state;

    const totalToday = todaysCards?.length ?? 0;
    const {
      verySmallDeckSize,
      smallDeckSize,
      mediumDeckSize,
      largeDeckSize,
      veryLargeDeckSize,
    } = this.getDeckSizes(readyToTest.length);
    const totalCount = readyToTestCount;

    let userWords: (UserWord & {
      nextTimeSortKey: number;
      lastSeenSortKey: number;
      nextTime: number;
    })[] = [];

    if (todaysCards && date) {
      for (const userWord of todaysCards) {
        const userWordJSON = userWord.viewJSON.value;

        let lastSeen = userWordJSON.previousIntervalDate;
        if (lastSeen) {
          lastSeen = new Date(
            new Date(
              dateUtils.parse(userWordJSON.previousIntervalDate)
            ).getTime() - userWordJSON.previousInterval
          );

          userWordJSON.lastSeenSortKey = lastSeen.getTime();
          lastSeen = Math.round(
            (date.getTime() - lastSeen.getTime()) / spacedRepetition.DAY
          );
          if (lastSeen <= 0) {
            userWordJSON.lastSeen = "New";
          } else if (lastSeen === 1) {
            userWordJSON.lastSeen = "Yesterday";
          } else {
            userWordJSON.lastSeen = lastSeen + " days ago";
          }
        } else {
          userWordJSON.lastSeen = "New";
        }
        userWordJSON.rememberedFirstTime = "Yes";
        const nextDate = new Date(dateUtils.parse(userWordJSON.nextDate));
        if (nextDate) {
          userWordJSON.nextTimeSortKey = nextDate.getTime();
          // `date` is the start of today, so flooring gives the day the word
          // falls due on — which is what the user sees, since cards become due
          // at the start of their scheduled day rather than at its exact time.
          const nextTime = Math.floor(
            (nextDate.getTime() - date.getTime()) / spacedRepetition.DAY
          );
          if (nextTime < 0) {
            userWordJSON.nextTime = "Now";
          } else if (nextTime === 0) {
            userWordJSON.nextTime = "Today";
          } else if (nextTime === 1) {
            userWordJSON.nextTime = "Tomorrow";
            if (lastSeen) {
              userWordJSON.rememberedFirstTime = "No";
            }
          } else {
            userWordJSON.nextTime = "In " + nextTime + " days";
          }
        }

        userWords.push(userWordJSON);
      }

      userWords = userWords.sort((a, b) => {
        const result = a.nextTimeSortKey - b.nextTimeSortKey;
        if (result === 0) {
          if (!a.lastSeenSortKey) {
            return -1;
          }
          if (!b.lastSeenSortKey) {
            return 1;
          }
          return a.lastSeenSortKey - b.lastSeenSortKey;
        } else {
          return result;
        }
      });
    }

    const message =
      totalToday > 0
        ? `You've recalled ${totalToday} word${totalToday === 1 ? "" : "s"} today. Nice work!`
        : undefined;

    const totalExists = totalToday > 0;

    const practiceSettingsRows: preact.JSX.Element[] = [];

    if (this.state.hasNewPracticeModes) {
      practiceSettingsRows.push(
        <tr>
          <td>
            <label>Mode</label>
          </td>
          <td>
            <select
              value={this.practiceType()}
              onChange={(event) =>
                user.save({ practiceType: event.currentTarget.value })
              }
            >
              {this.state.hasNewPracticeModes ? (
                <>
                  <option value="blitz">Blitz (no typing)</option>
                  <option value="new">Mastery (with typing)</option>
                </>
              ) : null}
              <option value="classic">Classic Flashcards</option>
              {!this.state.hasNewPracticeModes ? (
                <>
                  <option value="blitz">Blitz (no typing)</option>
                  <option value="new">Mastery (with typing)</option>
                </>
              ) : null}
            </select>
          </td>
        </tr>
      );
    }
    if (mediumDeckSize) {
      practiceSettingsRows.push(
        <tr>
          <td>
            <label>Length</label>
          </td>
          <td>
            <select
              value={user.state.value.practiceLength ?? "small"}
              onChange={(event) =>
                user.save({ practiceLength: event.currentTarget.value })
              }
            >
              <option value="verySmall">{verySmallDeckSize} words</option>
              {smallDeckSize ? (
                <option value="small">{smallDeckSize} words</option>
              ) : null}
              {mediumDeckSize ? (
                <option value="medium">{mediumDeckSize} words</option>
              ) : null}
              {largeDeckSize ? (
                <option value="large">{largeDeckSize} words</option>
              ) : null}
              {veryLargeDeckSize ? (
                <option value="veryLarge">{veryLargeDeckSize} words</option>
              ) : null}
            </select>
          </td>
        </tr>
      );
    }
    if (sources === "loading" || sources.length > 0) {
      const selectedSources = this.state.selectedSources ?? [];
      const loadedSources: any[] = sources === "loading" ? [] : sources;

      const selectedItems = selectedSources
        .map((source) => loadedSources.find((item) => item.source === source))
        .filter((item) => item !== undefined);

      const buttonLabel = (() => {
        if (selectedItems.length === 0) {
          return (
            <>
              All{" "}
              {totalCount !== undefined ? (
                <span class="sourceCount">({totalCount} words ready)</span>
              ) : null}
            </>
          );
        }
        if (selectedItems.length === 1) {
          const item = selectedItems[0];
          return (
            <>
              {sourceUtils.getSourceEmoji(item.source) + " "}
              {item.displayName}{" "}
              <span class="sourceCount">({item.count} words ready)</span>
            </>
          );
        }
        // No total: sources can share words (a shelf and one of its texts,
        // or one word saved from two texts), so summing them overstates the
        // session, and only the server knows the real union.
        return <>{selectedItems.length} sources</>;
      })();

      const openSourcePicker = () => {
        if (sources === "loading" || this.state.language === undefined) {
          return;
        }
        const language = this.state.language;
        // If any persisted selection lives past the collapsed window, open
        // the picker with the full list already visible so the user isn't
        // confused that their current choices are missing. Only the texts
        // collapse - shelves always render in full - so a selected shelf
        // finds no index here and leaves the list collapsed.
        const textSources = loadedSources.filter(
          (item) => !sourceUtils.isShelfSource(item.source)
        );
        const initiallyExpanded = selectedSources.some(
          (source) =>
            textSources.findIndex((item) => item.source === source) >=
            RECENTLY_READ_INITIAL_LIMIT
        );
        modalController.show(
          <Dialog heading="Choose sources">
            <SourcePickerModal
              sources={loadedSources}
              multiSelect={true}
              selectedSources={selectedSources}
              recentlyReadLimit={RECENTLY_READ_INITIAL_LIMIT}
              initiallyExpanded={initiallyExpanded}
              allWords={{
                count: totalCount ?? 0,
                onSelect: () => {},
              }}
              onConfirm={(picked: string[]) => {
                modalController.cancel(null, true);
                setStoredSelectedSources(language, picked);
                this.setState({ selectedSources: picked });
              }}
            />
          </Dialog>,
          { className: "sourcePickerDialog" }
        );
      };

      practiceSettingsRows.push(
        <tr>
          <td>
            <label>Source</label>
          </td>
          <td>
            <button
              type="button"
              class="sourcePickerButton"
              disabled={sources === "loading"}
              onClick={openSourcePicker}
            >
              <span class="sourcePickerButtonLabel">{buttonLabel}</span>
              <span class="sourcePickerButtonCaret">▾</span>
            </button>
          </td>
        </tr>
      );
    }

    return (
      <div>
        {sessionCompleteMessage}
        {sessionCompleteAudio}

        {newCardsAvailable ? (
          <h2>
            {!totalExists ? "Review your words" : message}
            {totalToday > 0 ? (
              <>
                <br />
                <small>
                  <i>Don't stop there, keep learning…</i>
                </small>
              </>
            ) : null}
          </h2>
        ) : (
          <h2>No new cards</h2>
        )}

        {newCardsAvailable ? (
          <div class="practiceControls">
            <PracticeButton
              href={practiceUtils.buildSessionUrl(
                user,
                this.state.selectedSources
              )}
              icon="/content/flashcardsIcon-v2.svg"
              title="Start practice"
              testID="learn-start-practice-button"
            />
            {practiceSettingsRows.length > 0 ? (
              <div>
                <table class="practiceOptionsTable">
                  <tbody>{practiceSettingsRows}</tbody>
                </table>
              </div>
            ) : null}
          </div>
        ) : null}

        <ChatPracticeView />

        {newCardsAvailable && (
          <div class="practiceEveryDay">
            <div id="weeklyChartContainerContainer" style={{ display: "none" }}>
              <h2>
                Practice <strong>every day</strong>
              </h2>

              <div id="weeklyChartContainer">
                <canvas id="weeklyChart" width="470" height="200"></canvas>
              </div>
            </div>

            {localMode === false && (
              <dailyReminders.DailyReminderSwitch
                label={
                  dailyReminders.byNotification()
                    ? "Remind me each day if I forget to practice"
                    : "Email me a daily reminder if I forget to practice"
                }
              />
            )}
          </div>
        )}

        {totalExists && localMode === true && (
          <div class="callout">
            <a
              href="#"
              class="clickable"
              data-clickable="signUpButton"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                signupModal.show({
                  stores,
                });
              }}
            >
              Sign Up For Free
            </a>{" "}
            to practice from another laptop, phone or tablet.
          </div>
        )}

        {totalExists && (
          <div class="reviewedToday">
            <h2>
              Reviewed {totalToday} word{totalToday === 1 ? "" : "s"} today
            </h2>
            <table>
              <thead>
                <tr>
                  <th>Word</th>
                  <th>Translation</th>
                  <th>Next due</th>
                </tr>
              </thead>
              <tbody>
                {userWords.map((word, index) => (
                  <tr key={index}>
                    <td>{word.word}</td>
                    <td>{word.translation}</td>
                    <td>{word.nextTime}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  practiceType = () => {
    return (
      stores.user.state.value.practiceType ??
      // Defaulting to "blitz" in newPractice experiment (since 2024-06-05)
      (this.state.hasNewPracticeModes ? "blitz" : "classic")
    );
  };
}

export default LearnPageView;
